/** IXD 편집기 - 미리보기 뷰 페이지
 *
 * `?reportId=` 는 편집 가능한 IXD 리포트, `?filingId=` 는 DART 공시 XBRL 열람본이다.
 * 후자는 값 편집 경로가 없어 읽기 전용 뷰로 간다.
 */

import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/toggle-ui-kit/button";
import queries from "@/queries";
import { notifyIxdMatchPreviewOpener } from "@/utils/ixd-workbook-match/tab-messages";
import { logIxdEditorAction } from "@/utils/usage-log";
import { IxdEditorStoreProvider } from "./editor-store-provider";
import { EditorContentView } from "./editor-content-view";
import { FilingContentView } from "./filing-content-view";

export const IxdEditorViewPage = () => {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("reportId");
  const filingId = searchParams.get("filingId");
  // 업로드 시점에 실어 보낸 파일명. 직접 붙여넣은 URL 에는 없을 수 있다.
  const filingFilename = searchParams.get("filename");
  const navigate = useNavigate();

  if (filingId) {
    return (
      <IxdEditorStoreProvider>
        <DartFilingViewContent filingId={filingId} filename={filingFilename} />
      </IxdEditorStoreProvider>
    );
  }

  if (!reportId) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="body-1-normal-regular text-label-alternative">
          reportId가 지정되지 않았습니다.
        </p>
        <Button
          variant="outlined"
          color="secondary"
          size="base"
          onClick={() => navigate("/ixd-editor")}
        >
          업로드 페이지로 이동
        </Button>
      </div>
    );
  }

  return (
    <IxdEditorStoreProvider>
      <IxdEditorViewContent reportId={reportId} />
    </IxdEditorStoreProvider>
  );
};

const IxdEditorViewContent = ({ reportId }: { reportId: string }) => {
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    ...queries.ixdEditor(reportId)._ctx.preview(),
  });

  // 미리보기 로드 성공 여부와 무관하게 들어온 것 자체를 센다 — 퍼널의 시작점
  useEffect(() => {
    logIxdEditorAction("페이지 진입");
  }, [reportId]);

  // 워크북 비교 탭으로 열렸는데 IXD 미리보기부터 실패하면 본문(useWorkbookCompare)까지
  // 가지 못한다 — 선택 화면이 "비교 준비 중"에 갇히지 않도록 이 구간만 여기서 알린다.
  useEffect(() => {
    if (isError) notifyIxdMatchPreviewOpener("failed");
  }, [isError]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full gap-2">
        <Loader2 className="w-5 h-5 animate-spin text-label-alternative" />
        <span className="body-1-normal-regular text-label-alternative">
          미리보기 데이터를 불러오는 중...
        </span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="body-1-normal-regular text-status-negative">
          {error instanceof Error
            ? error.message
            : "미리보기 데이터를 불러오는 중 오류가 발생했습니다."}
        </p>
        <Button
          variant="outlined"
          color="secondary"
          size="base"
          onClick={() => navigate("/ixd-editor")}
        >
          업로드 페이지로 이동
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <EditorContentView reportId={reportId} previewData={data} />
    </div>
  );
};

const DartFilingViewContent = ({
  filingId,
  filename,
}: {
  filingId: string;
  filename: string | null;
}) => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    ...queries.dartFilingView(filingId)._ctx.preview(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full gap-2">
        <Loader2 className="w-5 h-5 animate-spin text-label-alternative" />
        <span className="body-1-normal-regular text-label-alternative">
          공시 XBRL을 읽는 중...
        </span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="body-1-normal-regular text-status-negative">
          {error instanceof Error
            ? error.message
            : "공시 XBRL을 불러오는 중 오류가 발생했습니다."}
        </p>
        <Button
          variant="outlined"
          color="secondary"
          size="base"
          onClick={() => navigate("/ixd-editor")}
        >
          업로드 페이지로 이동
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <FilingContentView previewData={data} filename={filename} />
    </div>
  );
};
